import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { colors, dateRangeFilterOptions, months, strCode } from "../../consts";
import { dateUtil } from "../../utils";
import { useOnClickOutside } from "../../hooks";
import { t } from "../../services";

import { SVG, IconButton } from "../../components";
import {
  FiltersInputContainer,
  FiltersInputWrapper,
  CalendarIconWrapper,
  FiltersDropdownWrapper,
  FiltersTitle
} from "./styled";
import DateRangeFiltersDropdownOptions from "./DropdownOptions/DateRangeFiltersDropdownOptions";

const options = [
  { id: dateRangeFilterOptions.TODAY, value: strCode.TODAY_OPTION },
  { id: dateRangeFilterOptions.LAST_24_HOURS, value: strCode.LAST_24_HOURS_OPTION },

  { id: dateRangeFilterOptions.THIS_WEEK, value: strCode.THIS_WEEK_OPTION },
  { id: dateRangeFilterOptions.LAST_7_DAYS, value: strCode.LAST_7_DAYS_OPTION },

  { id: dateRangeFilterOptions.THIS_MONTH, value: strCode.THIS_MONTH_OPTION },
  { id: dateRangeFilterOptions.LAST_30_DAYS, value: strCode.LAST_30_DAYS_OPTION },

  { id: dateRangeFilterOptions.THIS_YEAR, value: strCode.THIS_YEAR_OPTION },
  { id: dateRangeFilterOptions.LAST_12_MONTHS, value: strCode.LAST_12_MONTHS_OPTION },

  { id: dateRangeFilterOptions.RANGE, value: strCode.RANGE_OPTION }
];

const getFormattedDate = (date) => {
  return `${date.getDate()} ${
    months.find((month) => month.id === date.getMonth())?.month
  } ${date.getFullYear()}`;
};

const getValue = (filterOption, from, to) => {
  if (filterOption === dateRangeFilterOptions.RANGE) {
    return `${getFormattedDate(from)} - ${getFormattedDate(to)}`;
  } else {
    return options.find((option) => option.id === filterOption)?.value;
  }
};

const DateRangeFiltersInput = ({ loading, onUpdateDateRange, title }) => {
  const [value, setValue] = useState("");

  const [filterOption, setFilterOption] = useState();
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();

  const [optionsVisible, setOptionsVisible] = useState(false);

  const selectedPeriod = useSelector(
    (store) => store.dashboardFilters.selectedFilters.selectedPeriod
  );
  const selectedFromDate = useSelector(
    (store) => store.dashboardFilters.selectedFilters.selectedFromDate
  );
  const selectedToDate = useSelector(
    (store) => store.dashboardFilters.selectedFilters.selectedToDate
  );

  useEffect(() => {
    if (selectedPeriod === dateRangeFilterOptions.RANGE) {
      setFromDate(selectedFromDate);
      setToDate(selectedToDate);
    }

    setFilterOption(selectedPeriod ? selectedPeriod : dateRangeFilterOptions.THIS_WEEK);
  }, [selectedPeriod]);

  const updateDateRange = useCallback(
    (option, from, to) => {
      if (!from || !to) {
        const { fromDate, toDate } = dateUtil.calculateRange(option);

        setFromDate(fromDate);
        setToDate(toDate);
      }

      if (from && to) {
        setValue(getValue(option, from, to));
        onUpdateDateRange(from, to, option);
      }
    },
    [setFromDate, setToDate, setValue]
  );

  useEffect(() => {
    updateDateRange(filterOption, fromDate, toDate);
  }, [filterOption, fromDate, toDate]);

  const optionsRef = useRef();
  useOnClickOutside(optionsRef, () => {
    setOptionsVisible(false);
  });

  const onApply = (option, from, to) => {
    setFilterOption(option);
    setFromDate(from);
    setToDate(to);

    setOptionsVisible(false);

    updateDateRange(option, from, to);
  };

  return (
    <>
      {title && <FiltersTitle>{title}</FiltersTitle>}
      <FiltersDropdownWrapper
        innerRef={optionsRef}
        width={298}
        position={"relative"}
        disabled={loading}>
        <div
          data-testid="date-range-filters-wrapper"
          data-visible={optionsVisible}
          onClick={() => setOptionsVisible(!optionsVisible)}>
          <FiltersInputContainer>
            <FiltersInputWrapper value={value} disabled={loading} isSmall>
              <CalendarIconWrapper>
                <SVG.CalendarIcon />
              </CalendarIconWrapper>

              <input
                id={"date-range-filters-input"}
                data-testid="input"
                type={"text"}
                value={value ? t(value) : ""}
                readOnly={true}
                disabled={loading}
              />

              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setOptionsVisible((prev) => !prev);
                }}>
                <SVG.DownArrowIcon color={colors.RAISIN_50} flipVert={optionsVisible} />
              </IconButton>
            </FiltersInputWrapper>
          </FiltersInputContainer>
        </div>

        <DateRangeFiltersDropdownOptions
          visible={optionsVisible}
          options={options}
          filterOption={filterOption}
          onApply={onApply}
          onCancel={() => setOptionsVisible(false)}
        />
      </FiltersDropdownWrapper>
    </>
  );
};

DateRangeFiltersInput.propTypes = {
  loading: PropTypes.bool.isRequired,
  onUpdateDateRange: PropTypes.func.isRequired,
  title: PropTypes.string
};

export default DateRangeFiltersInput;
